import React, { ReactNode } from 'react';
import Icon from '@/components/ui/AppIcon';
import WhatsAppLink from '@/components/common/WhatsAppLink';
import HQMapDialog from '@/components/common/HQMapDialog';
// EMAIL_ROUTING is re-imported when the routing table below is uncommented.
import { CONTACT, PHONE_LINES } from '@/lib/contact';

interface ContactMethod {
  icon: string;
  label: string;
  value: string | ReactNode;
  href: string;
  /** Set on phone rows only  - renders a WhatsApp button beside the number. */
  wa?: string | null;
  region?: string;
  /** Set on the address row  - renders the HQ map popup button. */
  map?: boolean;
}

export const ContactInfo: React.FC = () => {
  const contactMethods: ContactMethod[] = [
    {
      icon: 'EnvelopeIcon',
      label: 'Bulk quotes & enquiries',
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
    },
    // One `tel:` per line  - previously both numbers sat inside a single link,
    // so tapping the +44 number on mobile dialled India (audit F7).
    ...PHONE_LINES.map((line) => ({
      icon: 'PhoneIcon',
      label: `Phone (${line.region})`,
      value: line.display,
      href: `tel:${line.tel}`,
      wa: line.wa,
      region: line.region,
    })),
    {
      icon: 'MapPinIcon',
      label: 'HQ',
      value: `${CONTACT.addressLine1}, ${CONTACT.addressLine2}`,
      href: CONTACT.mapUrl,
      map: true,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-surface border border-border rounded-2xl p-6">
        <h3 className="font-headline text-xl font-bold text-foreground mb-3">Get in Touch</h3>

        <div className="space-y-2">
          {contactMethods.map((method) => (
            // Row wrapper, so the WhatsApp button sits *beside* the tel: link
            // rather than inside it  - nested anchors are invalid HTML.
            //
            // Spacing is tight on purpose. This sidebar is narrow between lg
            // and xl, and a 44px button plus the default p-4/gap-6 left the
            // number 107px when it needs 132, breaking it over three lines.
            // The anchor's right padding is redundant once a button follows it,
            // so rows with a button reclaim it. That buys back enough from
            // ~1080px up; below that the number still takes two lines, but it
            // breaks at the space ("+44" / "7438022070"), never mid-digit.
            <div
              key={method.label}
              className="flex items-center gap-1 rounded-xl bg-muted/50 transition duration-300 hover:bg-muted"
            >
              <a
                href={method.href}
                target={method.label === 'HQ' ? '_blank' : undefined}
                rel={method.label === 'HQ' ? 'noopener noreferrer' : undefined}
                className={`group flex min-w-0 flex-1 items-start gap-4 py-4 pl-4 ${
                  method.wa || method.map ? 'pr-1' : 'pr-4'
                }`}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon name={method.icon} size={20} />
                </span>
                <div className="min-w-0 space-y-3">
                  <p className="font-cta text-xs uppercase tracking-wider text-muted-foreground">
                    {method.label}
                  </p>
                  {/* Phone numbers wrap normally so they can only ever break at
                      the space, never mid-digit. Everything else gets
                      wrap-break-word, which the long address and email need. */}
                  <p
                    className={`font-body text-foreground group-hover:text-primary transition-colors ${
                      method.region ? '' : 'wrap-break-word'
                    }`}
                  >
                    {method.value}
                  </p>
                </div>
              </a>
              {method.map && (
                <HQMapDialog
                  analyticsId="contact_hq_map"
                  className="mr-2 bg-primary/10 text-primary hover:bg-primary/20"
                />
              )}
              {method.wa && method.region && (
                <WhatsAppLink
                  wa={method.wa}
                  region={method.region}
                  text={`Hi Flora Dine, I have a bulk enquiry about your compostable tableware. (${method.region} line)`}
                  analyticsId={`contact_whatsapp_${method.region.toLowerCase()}`}
                  className="mr-2 bg-[#25D366]/10 hover:bg-[#25D366]/20"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Commented for later Release: Routing table (audit T4): the site publishes several mailboxes, so say
          plainly which one to use rather than letting a buyer guess and bounce. */}
      {/* <div className="bg-surface border border-border rounded-2xl p-6">
        <h3 className="font-headline text-xl font-bold text-foreground mb-1">Who to email</h3>
        <p className="font-body text-sm text-muted-foreground mb-4">
          Every mailbox below is monitored on business days. {CONTACT.responsePromise}.
        </p>
        <dl className="divide-y divide-border">
          {EMAIL_ROUTING.map((row) => (
            <div key={row.email} className="grid gap-1 py-3 sm:grid-cols-2 sm:gap-4">
              <dt className="font-cta text-sm font-semibold text-foreground">{row.purpose}</dt>
              <dd>
                <a
                  href={`mailto:${row.email}`}
                  className="font-body text-sm text-primary underline underline-offset-4 wrap-break-word hover:text-primary/80"
                >
                  {row.email}
                </a>
                <p className="font-body text-xs text-muted-foreground">{row.detail}</p>
              </dd>
            </div>
          ))}
        </dl>
      </div> */}

      {/* Office hours with an explicit timezone and a UK/EU calling window
          (audit T8)  - hours with no timezone are unusable from another market. */}
      <div className="bg-surface border border-border rounded-2xl p-6">
        <h3 className="font-headline text-xl font-bold text-foreground mb-3">
          Office hours &amp; best time to call
        </h3>
        <ul className="space-y-2 font-body text-sm text-muted-foreground">
          <li>{CONTACT.officeHours.weekdays}</li>
          <li>{CONTACT.officeHours.saturday}</li>
        </ul>
      </div>
    </div>
  );
};
